/**
 * MemberGrowthChart — single-axis member count chart
 *
 * Accepts a ChartSeries prop for member growth count.
 * Revenue axis is suppressed (NOT_CONNECTED — requires Core Banking).
 * If no data, renders an empty state message.
 */
import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ChartSeries } from '../../../types/insightsTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface MemberGrowthChartProps {
  series?: ChartSeries;
}

const MemberGrowthChart: React.FC<MemberGrowthChartProps> = ({ series }) => {
  // No data or empty series — show empty state
  if (!series || series.data.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center text-slate-400 text-sm">
        No member growth data available
      </div>
    );
  }

  const formatLabel = (period: string): string => {
    try {
      const d = new Date(period);
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    } catch {
      return period;
    }
  };

  const data = {
    labels: series.data.map((d) => formatLabel(d.period)),
    datasets: [
      {
        label: series.label || 'Total Members',
        data: series.data.map((d) => d.value),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          color: '#64748b',
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function (context: any) {
            return `Members: ${context.parsed.y?.toLocaleString() ?? 0}`;
          },
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8', font: { size: 11 } },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: '#94a3b8',
          font: { size: 11 },
          callback: function (value: any) {
            return value.toLocaleString();
          },
        },
        title: {
          display: true,
          text: 'Members',
          color: '#64748b',
          font: { size: 12 },
        },
      },
      // Revenue axis (y1) removed — NOT_CONNECTED
    },
  };

  return (
    <div className="h-80">
      <Line data={data} options={options} />
    </div>
  );
};

export default MemberGrowthChart;